// @ts-nocheck
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  MapPin,
  Users,
  Calendar,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  Trophy as TrophyIcon,
  FileWarning,
  XCircle,
  Plus,
  CreditCard,
  DollarSign,
  Trophy,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { parseLocalDate } from '@/utils/formatters';

interface Props {
  organizationId: string | null;
}

interface AttentionItem {
  item_type: string;
  severity: string;
  label: string;
  detail: string;
  link_path: string;
  created_at: string;
}

interface EventRow {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
  venue_city: string | null;
  venue_state: string | null;
  unique_teams_registered: number;
  total_roster_size: number;
}

/* ─── Route helpers ──────────────────────────────────────── */
const schedulePath = (id: string) => `/admin-dashboard/events/${id}/schedule-setup`;
const registrationsPath = (id: string) => `/admin-dashboard/events/${id}/registrations`;
const scoringPath = (id: string) => `/staff/scoring/${id}`;

/* ─── Data hooks ─────────────────────────────────────────── */
function useAttentionItems(organizationId: string | null) {
  return useQuery({
    queryKey: ["triage-attention", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_dashboard_attention_items", {
        p_organization_id: organizationId,
      });
      if (error) throw error;
      const rows = (data as AttentionItem[]) || [];
      // De-duplicate items (e.g. pending schedule requests can appear twice)
      const seen = new Set<string>();
      return rows.filter((item) => {
        const key = `${item.item_type}|${item.label}|${item.detail}|${item.link_path}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
  });
}

function useStats(organizationId: string | null) {
  return useQuery({
    queryKey: ["triage-stats", organizationId],
    queryFn: async () => {
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const weekStartStr = weekStart.toISOString().split("T")[0];

      const baseFilter = (q: any) =>
        organizationId ? q.eq("organization_id", organizationId) : q;

      const [activeRes, revenueRes] = await Promise.all([
        baseFilter(
          supabase
            .from("v_event_admin_dashboard")
            .select("unique_teams_registered, total_collected, total_registrations, paid_registrations")
            .eq("is_active", true)
            .not("status", "in", '("completed","archived","cancelled")')
        ),
        baseFilter(
          supabase
            .from("v_event_admin_dashboard")
            .select("total_collected")
            .gte("created_at", weekStartStr)
        ),
      ]);

      const activeRows = activeRes.data || [];
      const revenueRows = revenueRes.data || [];

      return {
        activeEvents: activeRows.length,
        teams: activeRows.reduce(
          (s: number, r: any) => s + (r.unique_teams_registered || 0),
          0
        ),
        revenueThisWeek: revenueRows.reduce(
          (s: number, r: any) => s + (r.total_collected || 0),
          0
        ),
        unpaidRegistrations: activeRows.reduce(
          (s: number, r: any) =>
            s + Math.max(0, (r.total_registrations || 0) - (r.paid_registrations || 0)),
          0
        ),
      };
    },
  });
}

function useYourEvents(organizationId: string | null) {
  return useQuery({
    queryKey: ["triage-your-events", organizationId],
    queryFn: async () => {
      let q = supabase
        .from("v_event_admin_dashboard")
        .select(
          "id, name, start_date, end_date, status, venue_city, venue_state, unique_teams_registered, total_roster_size"
        )
        .eq("is_active", true)
        .not("status", "in", '("completed","archived","cancelled")')
        .order("start_date", { ascending: true })
        .limit(6);
      if (organizationId) q = q.eq("organization_id", organizationId);
      const { data, error } = await q;
      if (error) throw error;
      return (data as EventRow[]) || [];
    },
  });
}

/* ─── Helpers ────────────────────────────────────────────── */
function formatDateRange(start: string | null, end: string | null): string {
  if (!start) return "Date TBD";
  const s = new Date(start + "T00:00:00");
  const sFormatted = format(s, "MMM d");
  if (end && end !== start) {
    const e = new Date(end + "T00:00:00");
    const sameMonth =
      s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
    return sameMonth
      ? `${sFormatted}–${format(e, "d, yyyy")}`
      : `${sFormatted} – ${format(e, "MMM d, yyyy")}`;
  }
  return format(s, "MMM d, yyyy");
}

function AttentionIcon({ severity, itemType }: { severity: string; itemType: string }) {
  if (itemType === "failed_ingestion") return <XCircle className="h-5 w-5 text-destructive" />;
  if (itemType === "publish_blocker") return <FileWarning className="h-5 w-5 text-amber-600" />;
  if (severity === "high") return <AlertCircle className="h-5 w-5 text-destructive" />;
  if (severity === "medium") return <Clock className="h-5 w-5 text-amber-600" />;
  return <CalendarClock className="h-5 w-5 text-blue-600" />;
}

const PUBLISHED_STATUSES = [
  "published",
  "registration_open",
  "registrationOpen",
  "in_progress",
  "inProgress",
];

interface EventState {
  isPublished: boolean;
  isLive: boolean;
  needsSetup: boolean;
  pillLabel: string;
  pillTone: "live" | "published" | "draft" | "needs_setup";
}

function deriveEventState(ev: EventRow): EventState {
  const isPublished = PUBLISHED_STATUSES.includes(ev.status);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = ev.start_date ? parseLocalDate(ev.start_date + "T00:00:00") : null;
  const end = ev.end_date ? parseLocalDate(ev.end_date + "T00:00:00") : start;
  const inDateRange = !!start && today >= start && (!end || today <= end);
  const isLive =
    isPublished && (ev.status === "in_progress" || ev.status === "inProgress" || inDateRange);
  const needsSetup = !isPublished;

  if (isLive) return { isPublished, isLive, needsSetup, pillLabel: "Live", pillTone: "live" };
  if (isPublished)
    return { isPublished, isLive, needsSetup, pillLabel: "Published", pillTone: "published" };
  if (ev.status === "draft")
    return {
      isPublished,
      isLive,
      needsSetup,
      pillLabel: "Needs setup",
      pillTone: "needs_setup",
    };
  return {
    isPublished,
    isLive,
    needsSetup,
    pillLabel: "Draft",
    pillTone: "draft",
  };
}

/* ─── Stat card ──────────────────────────────────────────── */
function StatCard({
  icon: Icon,
  label,
  value,
  tint,
  isLoading,
  placeholder,
}: {
  icon: any;
  label: string;
  value: string | number;
  tint: string;
  isLoading?: boolean;
  placeholder?: boolean;
}) {
  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardContent className="flex items-center gap-4 p-5">
        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${tint}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">{label}</p>
          {isLoading ? (
            <Skeleton className="mt-1 h-7 w-16" />
          ) : (
            <p className="text-2xl font-bold leading-tight">
              {value}
              {placeholder && (
                <span className="ml-1 align-middle text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  not wired
                </span>
              )}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Status pill ────────────────────────────────────────── */
function StatusPill({ state }: { state: EventState }) {
  const toneMap: Record<string, string> = {
    live: "bg-emerald-100 text-emerald-700 border-emerald-300",
    published: "bg-green-50 text-green-700 border-green-200",
    needs_setup: "bg-amber-50 text-amber-700 border-amber-200",
    draft: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const tone = toneMap[state.pillTone] || toneMap.draft;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 select-none items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
        tone
      )}
    >
      {state.pillTone === "live" && (
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      )}
      {state.pillLabel}
    </span>
  );
}

/* ─── Shortcut pill ──────────────────────────────────────── */
function ShortcutPill({
  icon: Icon,
  label,
  to,
  color = "default",
  disabled = false,
}: {
  icon: any;
  label: string;
  to?: string;
  color?: "schedule" | "registrations" | "scoring" | "default";
  disabled?: boolean;
}) {
  const colorMap: Record<string, string> = {
    schedule: "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100",
    registrations: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    scoring: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100",
    default: "border-border bg-background text-foreground hover:bg-muted/60",
  };
  const base =
    "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors";
  const tone = disabled
    ? "cursor-not-allowed border border-border bg-muted/50 text-muted-foreground/60"
    : colorMap[color];

  if (disabled || !to) {
    return (
      <span className={cn(base, tone)} aria-disabled={disabled}>
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }
  return (
    <Link to={to} className={cn(base, tone)} onClick={(e) => e.stopPropagation()}>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </Link>
  );
}

/* ─── Jump-to button (Option B) ──────────────────────────── */
function JumpToButton({
  icon: Icon,
  label,
  events,
  pathFor,
  color = "default",
}: {
  icon: any;
  label: string;
  events: EventRow[];
  pathFor: (id: string) => string;
  color?: "schedule" | "registrations" | "scoring" | "default";
}) {
  const navigate = useNavigate();
  const single = events.length === 1;

  const colorMap: Record<string, string> = {
    schedule: "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100",
    registrations: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    scoring: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100",
    default: "border-border bg-muted/30 text-foreground/80 hover:bg-muted/60",
  };

  const btn = (
    <button
      type="button"
      className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${colorMap[color]}`}
      disabled={events.length === 0}
      onClick={single ? () => navigate(pathFor(events[0].id)) : undefined}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );

  if (events.length <= 1) return btn;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{btn}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 rounded-xl">
        <DropdownMenuLabel>Choose an event</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {events.map((ev) => (
          <DropdownMenuItem
            key={ev.id}
            className="cursor-pointer rounded-lg"
            onClick={() => navigate(pathFor(ev.id))}
          >
            <span className="truncate">{ev.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ─── Sections ───────────────────────────────────────────── */
function AttentionSection({
  attention,
  attentionLoading,
  onNavigate,
}: {
  attention: AttentionItem[] | undefined;
  attentionLoading: boolean;
  onNavigate: (p: string) => void;
}) {
  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Needs your attention</CardTitle>
      </CardHeader>
      <CardContent>
        {attentionLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-2xl" />
            ))}
          </div>
        ) : !attention || attention.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-emerald-50 py-10 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="font-semibold">All caught up</p>
            <p className="text-sm text-muted-foreground">
              Nothing needs your attention right now.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {attention.map((item, i) => {
              const tint: Record<string, string> = {
                high: "bg-destructive/5 hover:bg-destructive/10",
                medium: "bg-amber-500/5 hover:bg-amber-500/10",
                low: "bg-blue-500/5 hover:bg-blue-500/10",
              };
              return (
                <button
                  key={i}
                  onClick={() => onNavigate(item.link_path)}
                  className={`flex w-full items-center gap-4 rounded-2xl border border-border/50 p-4 text-left transition-colors ${
                    tint[item.severity] || tint.low
                  }`}
                >
                  <AttentionIcon severity={item.severity} itemType={item.item_type} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{item.label}</p>
                    <p className="truncate text-sm text-muted-foreground">{item.detail}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function YourEventsSection({
  events,
  eventsLoading,
}: {
  events: EventRow[] | undefined;
  eventsLoading: boolean;
}) {
  const activeEvents = events || [];

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg">Your events</CardTitle>
        <Link
          to="/admin-dashboard/events"
          className="text-sm font-medium text-primary hover:underline"
        >
          View all
        </Link>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Jump-to quick row (Option B) */}
        {!eventsLoading && activeEvents.length > 0 && (
          <div className="space-y-2 rounded-2xl bg-muted/30 p-3">
            <p className="text-xs font-medium text-muted-foreground">Jump to</p>
            <div className="flex gap-2">
              <JumpToButton icon={CalendarDays} label="Schedules" events={activeEvents} pathFor={schedulePath} color="schedule" />
              <JumpToButton icon={ClipboardList} label="Registrations" events={activeEvents} pathFor={registrationsPath} color="registrations" />
              <JumpToButton icon={TrophyIcon} label="Scoring" events={activeEvents} pathFor={scoringPath} color="scoring" />
            </div>
          </div>
        )}

        {eventsLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-28 w-full rounded-2xl" />
            ))}
          </div>
        ) : activeEvents.length === 0 ? (
          <div className="rounded-2xl bg-muted/40 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              No active events. Create one to get started.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {activeEvents.map((ev) => {
              const venue = [ev.venue_city, ev.venue_state].filter(Boolean).join(", ");
              const state = deriveEventState(ev);
              return (
                <div
                  key={ev.id}
                  className="flex flex-col gap-3 rounded-2xl border border-border/50 p-4 transition-colors hover:bg-muted/30"
                >
                  <div className="flex items-start gap-3">
                    <Link
                      to={`/admin-dashboard/events/${ev.id}/edit`}
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${state.pillTone === "live" ? "bg-emerald-50 text-emerald-600" : state.pillTone === "published" ? "bg-blue-50 text-blue-600" : state.pillTone === "needs_setup" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}
                    >
                      <Calendar className="h-5 w-5" />
                    </Link>
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/admin-dashboard/events/${ev.id}/edit`}
                        className="block truncate text-sm font-semibold hover:underline"
                      >
                        {ev.name}
                      </Link>
                      <p className="truncate text-xs text-muted-foreground">
                        {formatDateRange(ev.start_date, ev.end_date)}
                        {venue && (
                          <>
                            {" · "}
                            <MapPin className="inline h-3 w-3" /> {venue}
                          </>
                        )}
                        {" · "}
                        {ev.unique_teams_registered > 0 ? `${ev.unique_teams_registered} teams` : "No teams yet"}
                      </p>
                    </div>
                    <StatusPill state={state} />
                  </div>

                  {/* Per-event shortcuts */}
                  <div className="flex flex-wrap gap-2">
                    <ShortcutPill
                      icon={CalendarDays}
                      label="Schedule"
                      to={schedulePath(ev.id)}
                      color="schedule"
                    />
                    <ShortcutPill
                      icon={ClipboardList}
                      label="Registrations"
                      to={registrationsPath(ev.id)}
                      color="registrations"
                    />
                    <ShortcutPill
                      icon={TrophyIcon}
                      label="Scoring"
                      to={state.isPublished ? scoringPath(ev.id) : undefined}
                      color="scoring"
                      disabled={!state.isPublished}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Main component ─────────────────────────────────────── */
type SectionOrder = "events" | "attention";

export function OperatorDashboardNew({ organizationId }: Props) {
  const navigate = useNavigate();
  const { profile, user } = useAuth();

  const { data: attention, isLoading: attentionLoading } = useAttentionItems(organizationId);
  const { data: stats, isLoading: statsLoading } = useStats(organizationId);
  const { data: events, isLoading: eventsLoading } = useYourEvents(organizationId);

  const firstName = profile?.name?.split(" ")[0] || "there";

  const storageKey = `rl_dashboard_order_${user?.id || "anon"}`;
  const [order, setOrder] = useState<SectionOrder>("events");

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved === "events" || saved === "attention") setOrder(saved);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const handleOrderChange = (value: string) => {
    if (value !== "events" && value !== "attention") return;
    setOrder(value as SectionOrder);
    try {
      localStorage.setItem(storageKey, value);
    } catch {
      /* ignore */
    }
  };

  const attentionSection = (
    <AttentionSection
      attention={attention}
      attentionLoading={attentionLoading}
      onNavigate={navigate}
    />
  );
  const eventsSection = (
    <YourEventsSection events={events} eventsLoading={eventsLoading} />
  );

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 space-y-6">
      {/* Header row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Welcome back, {firstName}</h1>
          <p className="text-sm text-muted-foreground">Here's what needs you today.</p>
        </div>
        <Button asChild size="lg" className="rounded-full self-start sm:self-auto">
          <Link to="/admin-dashboard/events/new">
            <Plus className="h-4 w-4" />
            New event
          </Link>
        </Button>
      </div>

      {/* At-a-glance stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Trophy}
          label="Active events"
          value={stats?.activeEvents ?? 0}
          tint="bg-primary/10 text-primary"
          isLoading={statsLoading}
        />
        <StatCard
          icon={Users}
          label="Teams"
          value={(stats?.teams ?? 0).toLocaleString()}
          tint="bg-blue-500/10 text-blue-600"
          isLoading={statsLoading}
        />
        <StatCard
          icon={CreditCard}
          label="Unpaid registrations"
          value={(stats?.unpaidRegistrations ?? 0).toLocaleString()}
          tint="bg-amber-500/10 text-amber-600"
          isLoading={statsLoading}
        />
        <StatCard
          icon={DollarSign}
          label="This week's revenue"
          value={`$${((stats?.revenueThisWeek ?? 0) / 100).toLocaleString(undefined, {
            maximumFractionDigits: 0,
          })}`}
          tint="bg-emerald-500/10 text-emerald-600"
          isLoading={statsLoading}
        />
      </div>

      {/* Order toggle */}
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-muted-foreground">Show first</span>
        <ToggleGroup
          type="single"
          value={order}
          onValueChange={handleOrderChange}
          className="rounded-full bg-muted/50 p-1"
        >
          <ToggleGroupItem
            value="events"
            className="rounded-full px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/60 data-[state=on]:bg-cyan-subtle data-[state=on]:font-medium data-[state=on]:text-cyan data-[state=on]:shadow-sm"
          >
            Your events
          </ToggleGroupItem>
          <ToggleGroupItem
            value="attention"
            className="rounded-full px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/60 data-[state=on]:bg-cyan-subtle data-[state=on]:font-medium data-[state=on]:text-cyan data-[state=on]:shadow-sm"
          >
            Needs your attention
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Ordered sections */}
      {order === "events" ? (
        <>
          {eventsSection}
          {attentionSection}
        </>
      ) : (
        <>
          {attentionSection}
          {eventsSection}
        </>
      )}
    </div>
  );
}
